import * as React from 'react';
import * as ToastPrimitives from '@radix-ui/react-toast';
import { cva, type VariantProps } from 'class-variance-authority';
import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';

const ToastProvider = ToastPrimitives.Provider;

/**
 * Toast placement (#2398, ruling D-2398, 2026-10-09). Top-right at top-20 put
 * toasts over the sticky record header's "Record a decision" and, on phones,
 * over the header icons. Now:
 *   - phones: bottom-centre, above the parent BottomNav (h-16 plus the
 *     bottom safe area, BottomNav.tsx);
 *   - sm and up: bottom-right, lifted 64 px so the cards (inside the 16 px
 *     padding) clear the 72 px widget launcher zone (MainLayout #2278).
 * The viewport is pointer-events-none so its padding band never takes a
 * click; each toast card is pointer-events-auto (toastVariants).
 *
 * While a modal dialog is open, phones move the viewport to the top edge
 * (#2398 follow-up, 2026-10-10 walk): on a phone a dialog's footer is the
 * bottom of the screen, and the decision toast sat on the notice dialog's
 * "Send notice" and Cancel — the very next action. The page behind a modal
 * is inert, so the top edge is free. Desktop keeps bottom-right.
 * At the top the card may cover the dialog's title but never its header
 * controls (#2398 round 3): the viewport pads its right edge past the
 * leftmost control in the dialog's header band, the Close X included.
 *
 * Phones (#2757): stacked document toasts (~138 px each) climbed over the
 * wizard's "Save & continue". On phones the card is compact and only the
 * newest card shows (older ones still time out, hidden). On a touch screen
 * the Close X is always visible (no hover there) and a 44 px target.
 */
const OPEN_MODAL_SELECTOR =
  '[role="dialog"][data-state="open"], [role="alertdialog"][data-state="open"]';

function hasOpenModal(): boolean {
  return typeof document !== 'undefined' && document.querySelector(OPEN_MODAL_SELECTOR) !== null;
}

/** Controls whose top edge is this close to the dialog's top are its header band. */
const HEADER_BAND_PX = 64;
/** Gap between the toast card's right edge and the leftmost header control. */
const HEADER_CLEAR_GAP_PX = 8;
const CONTROL_SELECTOR =
  'button, a[href], input, select, textarea, [role="button"], [tabindex]:not([tabindex="-1"])';

/**
 * How far the phone viewport's right padding must reach so a top toast ends
 * left of every control in an open dialog's header band (#2398 round 3: the
 * top toast sat on a tall dialog's Close X). Only controls in the right half
 * of the dialog count: the title side is the part a toast may cover, and a
 * full-width field cannot be cleared by a side inset. Undefined when no
 * dialog is open or its header band holds no control. The viewport applies
 * it as inline-end padding (`pe-`, the #2109 logical-utilities ratchet); with
 * no right-to-left locale shipped, inline-end is the right edge, where the
 * dialog's X sits (`right-4`).
 */
function measureHeaderClearance(): number | undefined {
  if (typeof document === 'undefined' || typeof window === 'undefined') return undefined;
  let minLeft = Number.POSITIVE_INFINITY;
  document.querySelectorAll(OPEN_MODAL_SELECTOR).forEach((dialog) => {
    const box = dialog.getBoundingClientRect();
    const midX = box.left + box.width / 2;
    dialog.querySelectorAll(CONTROL_SELECTOR).forEach((control) => {
      const r = control.getBoundingClientRect();
      if (r.width === 0 && r.height === 0) return;
      if (r.top - box.top > HEADER_BAND_PX) return;
      if (r.left < midX) return;
      minLeft = Math.min(minLeft, r.left);
    });
  });
  if (!Number.isFinite(minLeft)) return undefined;
  return Math.max(16, Math.ceil(window.innerWidth - minLeft + HEADER_CLEAR_GAP_PX));
}

interface ModalState {
  open: boolean;
  /** px the phone viewport pads on the right while a dialog is open. */
  clearRight?: number;
}

function readModalState(): ModalState {
  const open = hasOpenModal();
  return { open, clearRight: open ? measureHeaderClearance() : undefined };
}

/**
 * True while a Radix dialog or alert dialog is open anywhere on the page, and
 * the right clearance its header controls need. Re-measured on DOM changes,
 * on resize, and when the dialog's open animation ends (mid-zoom rects are
 * not where the controls settle).
 */
function useModalState(): ModalState {
  const [state, setState] = React.useState<ModalState>(readModalState);
  React.useEffect(() => {
    if (typeof MutationObserver === 'undefined') return;
    const update = () =>
      setState((prev) => {
        const next = readModalState();
        return prev.open === next.open && prev.clearRight === next.clearRight ? prev : next;
      });
    update();
    const observer = new MutationObserver(update);
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['data-state', 'role'],
    });
    window.addEventListener('resize', update);
    document.addEventListener('animationend', update, true);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', update);
      document.removeEventListener('animationend', update, true);
    };
  }, []);
  return state;
}

const ToastViewport = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Viewport>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Viewport>
>(({ className, style, ...props }, ref) => {
  const { open: modalOpen, clearRight } = useModalState();
  const clearStyle =
    modalOpen && clearRight !== undefined
      ? ({ '--toast-clear-right': `${clearRight}px` } as React.CSSProperties)
      : undefined;
  return (
    <ToastPrimitives.Viewport
      ref={ref}
      data-modal-open={modalOpen ? 'true' : undefined}
      style={clearStyle ? { ...clearStyle, ...style } : style}
      className={cn(
        'pointer-events-none fixed inset-x-0 top-auto bottom-[calc(4rem+env(safe-area-inset-bottom))] z-[100] flex max-h-screen w-full flex-col p-4 max-sm:pe-20 max-sm:data-[modal-open=true]:top-[env(safe-area-inset-top)] max-sm:data-[modal-open=true]:bottom-auto max-sm:data-[modal-open=true]:pe-[var(--toast-clear-right,1rem)] sm:left-auto sm:right-0 sm:bottom-16 md:max-w-[420px] max-sm:[&>li:not(:last-child)]:hidden',
        className
      )}
      {...props}
    />
  );
});
ToastViewport.displayName = ToastPrimitives.Viewport.displayName;

const toastVariants = cva(
  'group pointer-events-auto relative flex w-full items-center justify-between space-x-4 overflow-hidden rounded-md border p-6 pr-8 max-sm:py-3 max-sm:ps-4 [@media(pointer:coarse)]:pe-14 shadow-lg transition-all data-[swipe=cancel]:translate-x-0 data-[swipe=end]:translate-x-[var(--radix-toast-swipe-end-x)] data-[swipe=move]:translate-x-[var(--radix-toast-swipe-move-x)] data-[swipe=move]:transition-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[swipe=end]:animate-out data-[state=closed]:fade-out-80 data-[state=closed]:slide-out-to-right-full data-[state=open]:slide-in-from-bottom-full',
  {
    variants: {
      variant: {
        default: 'border bg-background text-foreground',
        destructive:
          'destructive group border-destructive bg-destructive text-destructive-foreground',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  }
);

const Toast = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Root>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Root> &
    VariantProps<typeof toastVariants>
>(({ className, variant, ...props }, ref) => {
  return (
    <ToastPrimitives.Root
      ref={ref}
      className={cn(toastVariants({ variant }), className)}
      {...props}
    />
  );
});
Toast.displayName = ToastPrimitives.Root.displayName;

const ToastAction = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Action>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Action>
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Action
    ref={ref}
    className={cn(
      'inline-flex h-8 shrink-0 items-center justify-center rounded-md border bg-transparent px-3 text-body font-medium ring-offset-background transition-colors hover:bg-secondary focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 group-[.destructive]:border-muted/40 group-[.destructive]:hover:border-destructive/30 group-[.destructive]:hover:bg-destructive group-[.destructive]:hover:text-destructive-foreground group-[.destructive]:focus:ring-destructive',
      className
    )}
    {...props}
  />
));
ToastAction.displayName = ToastPrimitives.Action.displayName;

const ToastClose = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Close>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Close>
>(({ className, ...props }, ref) => {
  // #2424: the X had no accessible name; same key as the dialog close.
  const { t } = useTranslation('common');
  return (
    <ToastPrimitives.Close
      ref={ref}
      aria-label={t('actions.close')}
      className={cn(
        'absolute right-2 top-2 inline-flex items-center justify-center rounded-md p-1 text-foreground/50 opacity-0 transition-opacity hover:text-foreground focus:opacity-100 focus:outline-none focus:ring-2 group-hover:opacity-100 [@media(hover:none)]:opacity-100 [@media(pointer:coarse)]:right-1 [@media(pointer:coarse)]:top-1 [@media(pointer:coarse)]:h-11 [@media(pointer:coarse)]:w-11group-[.destructive]:text-destructive-foreground/70 group-[.destructive]:hover:text-destructive-foreground group-[.destructive]:focus:ring-destructive-foreground/40 group-[.destructive]:focus:ring-offset-destructive',
        className
      )}
      toast-close=""
      {...props}
    >
      <X className="h-4 w-4" aria-hidden="true" />
    </ToastPrimitives.Close>
  );
});
ToastClose.displayName = ToastPrimitives.Close.displayName;

const ToastTitle = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Title>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Title>
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Title
    ref={ref}
    className={cn('text-body font-semibold', className)}
    {...props}
  />
));
ToastTitle.displayName = ToastPrimitives.Title.displayName;

const ToastDescription = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Description>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Description>
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Description
    ref={ref}
    className={cn('text-body opacity-90', className)}
    {...props}
  />
));
ToastDescription.displayName = ToastPrimitives.Description.displayName;

type ToastProps = React.ComponentPropsWithoutRef<typeof Toast>;

type ToastActionElement = React.ReactElement<typeof ToastAction>;

export {
  type ToastProps,
  type ToastActionElement,
  ToastProvider,
  ToastViewport,
  Toast,
  ToastTitle,
  ToastDescription,
  ToastClose,
  ToastAction,
};
